<template>
  <div
    ref="svgWrapper"
    class="inline-svg-wrapper"
    v-html="svgContent"
  />
</template>

<script>
// Shared across all instances to avoid re-fetching the same SVG
const svgCache = new Map()
</script>

<script setup>
const props = defineProps({
  src: {
    type: String,
    default: ''
  },
  activeId: {
    type: String,
    default: ''
  }
})

const config = useRuntimeConfig()
const svgWrapper = ref(null)
const svgContent = ref('')

// Tracks the original inline display value removed to make a hidden element visible
const savedInlineDisplay = new Map()
// Tracks the original DOM position of elements moved to fix z-order
const savedDomPosition = new Map()
// Tracks <text> siblings moved on top of the highlight so labels stay in the foreground
const savedTextPositions = new Map()

/**
 * Converts a full PIM asset URL to a same-origin proxy path so the fetch
 * is made server-side and is not subject to CORS restrictions.
 */
function toProxyUrl(url) {
  const base = config.public.pimDataBaseUrl?.replace(/\/$/, '')
  if (base && url.startsWith(base)) {
    return '/api/pim/' + url.slice(base.length).replace(/^\//, '')
  }
  return url
}

function applyHighlight(id) {
  if (!process.client || !svgWrapper.value) {
    return
  }
  svgWrapper.value.querySelectorAll('.svg-highlight').forEach(el => {
    el.classList.remove('svg-highlight')
    // Restore inline display that was removed to show the element
    if (savedInlineDisplay.has(el)) {
      el.style.setProperty('display', savedInlineDisplay.get(el))
      savedInlineDisplay.delete(el)
    }
    // Restore original DOM position if it was moved for z-order
    const pos = savedDomPosition.get(el)
    if (pos) {
      pos.parent.insertBefore(el, pos.nextSibling)
      savedDomPosition.delete(el)
    }
  })
  // Restore text labels after highlights are back in place (save-time nextSibling is valid again)
  savedTextPositions.forEach((pos, text) => {
    pos.parent.insertBefore(text, pos.nextSibling)
  })
  savedTextPositions.clear()
  if (id) {
    highlightAllComponents(id)
  }
}

const logSpecialCase = (message) => {
  try {
    const {$sentry} = useNuxtApp()
    if ($sentry) {
      $sentry.captureMessage(message, {
        level: 'warning'
      })
    }
  } catch (error) {
    throw new Error(`Failed to log special case: ${error.message}`)
  }
}

// highlight all elements with the same id as activeId, to support cases where multiple elements share the same filter id
const highlightAllComponents = (id) => {
  if (id) {
    const escapedId = CSS.escape(id)
    const highlightedElements = [
      ...svgWrapper.value.querySelectorAll(`[id="${escapedId}"], [id^="${escapedId}__"]`)
    ]
    if (highlightedElements.length > 0) {
      const affectedParents = new Set(highlightedElements.map(el => el.parentNode))

      // Save text positions BEFORE any DOM mutation so nextSibling references
      // reflect the original document order and remain valid during restore
      affectedParents.forEach(parent => {
        [...parent.children]
          .filter(child => child.tagName?.toLowerCase() === 'text')
          .forEach(text => {
            savedTextPositions.set(text, {parent, nextSibling: text.nextSibling})
          })
      })

      highlightedElements.forEach(el => {
        // Case 1: element is hidden via inline display:none — remove it so the CSS
        // class can make it visible (inline styles can win over !important in SVG contexts)
        const inlineDisplay = el.style.getPropertyValue('display')
        if (inlineDisplay) {
          savedInlineDisplay.set(el, inlineDisplay)
          el.style.removeProperty('display')
        }

        // Case 2: move element to the very end of its parent so it renders on top
        // of all siblings (SVG painter's model). This handles both background-color
        // rects at group level AND drawing groups (e.g. Ebene_4) at SVG root level
        // that would otherwise paint over the highlight.
        savedDomPosition.set(el, {parent: el.parentNode, nextSibling: el.nextSibling})
        el.parentNode.appendChild(el)

        el.classList.add('svg-highlight')
      })

      // Move all <text> siblings to the very end so labels render on top of the
      // highlight rect (SVG painter's model)
      affectedParents.forEach(parent => {
        [...parent.children]
          .filter(child => child.tagName?.toLowerCase() === 'text')
          .forEach(text => parent.appendChild(text))
      })
    } else {
      logSpecialCase(`Design sketch: Filter id "${id}" not found in "${props.src}".`)
    }
  }
}

watch(() => props.src, async (url) => {
  if (!url) {
    svgContent.value = ''
    return
  }
  const proxyUrl = toProxyUrl(url)
  if (svgCache.has(proxyUrl)) {
    svgContent.value = svgCache.get(proxyUrl)
    return
  }
  try {
    const text = await $fetch(proxyUrl, {responseType: 'text'})
    const content = typeof text === 'string' ? text : ''
    svgCache.set(proxyUrl, content)
    svgContent.value = content
  } catch {
    svgContent.value = ''
  }
}, {immediate: true})

watch(svgContent, () => {
  // SVG DOM is replaced entirely; discard all stale references
  savedInlineDisplay.clear()
  savedDomPosition.clear()
  savedTextPositions.clear()
  nextTick(() => applyHighlight(props.activeId))
})

watch(() => props.activeId, (newId) => {
  nextTick(() => applyHighlight(newId))
})
</script>

<style lang="scss">
.inline-svg-wrapper {
    width: 100%;

    svg {
        width: 100%;
        height: auto;
        display: block;
    }

    .svg-highlight {
        display: block!important;
        fill: $color__text__link!important;
    }
}
</style>
